import api, { ApiResponse } from './index'
import {
    SubscriptionPlan,
    PaymentInitializeRequest,
    PaymentInitializeResponse,
    PaymentVerifyRequest,
    PaymentVerifyResponse,
    SubscriptionResponse,
    PaymentHistoryItem,
    UpgradeTierRequest,
    CancelSubscriptionRequest,
} from '@/types/subscription'

/**
 * Subscription API endpoints
 * Handles all subscription tier management, payments, and upgrades
 */
export const subscriptionApi = {
    /**
     * Get all available subscription plans
     */
    getPlans: (): Promise<ApiResponse<{ plans: SubscriptionPlan[] }>> =>
        api.get('/subscriptions/plans'),

    /**
     * Get current user's subscription details
     */
    getCurrentSubscription: (): Promise<ApiResponse<{ subscription: SubscriptionResponse }>> =>
        api.get('/subscriptions/current'),

    /**
     * Initialize payment for subscription upgrade
     */
    initializePayment: (
        data: PaymentInitializeRequest
    ): Promise<ApiResponse<PaymentInitializeResponse>> =>
        api.post('/subscriptions/initialize', data),

    /**
     * Verify payment and activate subscription
     */
    verifyPayment: (
        data: PaymentVerifyRequest
    ): Promise<ApiResponse<PaymentVerifyResponse>> =>
        api.post('/subscriptions/verify', data),

    /**
     * Upgrade to a new subscription tier
     */
    upgradeTier: (
        data: UpgradeTierRequest
    ): Promise<ApiResponse<{ subscription: SubscriptionResponse }>> =>
        api.post('/subscriptions/upgrade', data),

    /**
     * Cancel current subscription
     */
    cancelSubscription: (
        data: CancelSubscriptionRequest
    ): Promise<ApiResponse<{ message: string }>> =>
        api.post('/subscriptions/cancel', data),

    /**
     * Get payment history for current user
     */
    getPaymentHistory: (params?: {
        page?: number
        limit?: number
    }): Promise<
        ApiResponse<{
            payments: PaymentHistoryItem[]
            totalCount: number
            currentPage: number
            totalPages: number
        }>
    > =>
        api.get('/subscriptions/history', { params }),

    /**
     * Get transaction list (with optional filters)
     */
    getTransactions: (params?: {
        page?: number
        limit?: number
        status?: string
        tier?: string
    }): Promise<
        ApiResponse<{
            transactions: PaymentHistoryItem[]
            totalCount: number
            currentPage: number
            totalPages: number
        }>
    > =>
        api.get('/subscriptions/transactions', { params }),
}